import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import api from "service/api";
import { ClientParametersDto } from "library/types/types";
import { getGroupInvoiceErrorMessage } from "./useCreateGroupInvoice";

// Backend DTO matching C# InvoiceRequestDto (the InvoiceNumber is assigned by the backend)
interface InvoiceRequestDto {
  /** empty = use the invoice number generated by the backend */
  DocumentNumber: string;
  Insurer: string;
  Period: number;
  Payments: number;
  Premium: number;
  Discount: number;
  DiscountPercent: number;
  DueDate: string; // ISO date string
  Deductible: string;
  VariantID: number; // Variant index (0 for MTPL, actual variant index for CASCO)
  Payer: ClientParametersDto; // The party the invoice is addressed to (mandatory)
}

interface CreateInvoiceParams {
  offerId: string;
  insuranceLine: "mtpl" | "casco";
  requestData: InvoiceRequestDto;
}

export function useCreateInvoice() {
  const { t } = useTranslation();

  return useMutation<any, Error, CreateInvoiceParams>({
    mutationFn: async ({
      offerId,
      insuranceLine,
      requestData,
    }: CreateInvoiceParams) => {
      const response = await api.post(
        `/invoices/${offerId}/${insuranceLine}`,
        requestData
      );
      return response.data;
    },
    onSuccess: (data) => {
      console.log("Invoice created successfully:", data);
      toast.success(
        t("qtg.invoice.createSuccess", "Invoice created successfully")
      );
    },
    onError: (error: any) => {
      console.error("Error creating invoice:", error);
      // validation failures (e.g. incomplete payer data) come back as an array of errors
      toast.error(
        getGroupInvoiceErrorMessage(
          error,
          t(
            "qtg.invoice.createError",
            "Failed to create invoice. Please try again."
          )
        )
      );
    },
  });
}
